import { InitiativePlain, MaterialityMap } from '../types/initiative';
import { MetricGroupDropdownItem } from '@g17eco/types/metricGroup';
import { DataPeriods } from '@utils/dataPeriods';
import { g17ecoApi, transformResponse } from './g17ecoApi';
import {
  UpgradeDetails,
  UpgradeResult,
  PlanUpgrade,
  UpgradeRequestBase,
  PreviewUpgradeResult
} from '../types/subscriptions';
import { SurveyType } from '@g17eco/types/surveyCommon';
import { UnitConfig } from '@g17eco/types/survey';

export const initiativeApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: ['initiative', 'surveys-periods', 'initiative-upgrade', 'metric-groups-dropdown'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getInitiativeById: builder.query<InitiativePlain, string>({
        transformResponse,
        query: (initiativeId) => {
          return {
            url: `/initiatives/${initiativeId}`,
            method: 'get',
          };
        },
        providesTags: ['initiative'],
      }),
      getAvailablePeriods: builder.query<DataPeriods[], { initiativeId: string; surveyType: SurveyType }>({
        transformResponse,
        query: ({ initiativeId, surveyType }) => ({
          url: `/initiatives/${initiativeId}/surveys-periods/${surveyType}`,
          method: 'get',
        }),
        providesTags: ['surveys-periods'],
      }),
      getUpgradeDetails: builder.query<UpgradeDetails, UpgradeRequestBase>({
        transformResponse,
        query: ({ initiativeId, featureCode }) => {
          return {
            url: `/initiatives/${initiativeId}/subscription/upgrade/${featureCode}`,
            method: 'get',
          };
        },
        providesTags: ['initiative-upgrade'],
      }),
      upgradePlan: builder.mutation<UpgradeResult, PlanUpgrade>({
        transformResponse,
        query: (planUpgrade) => {
          return {
            url: `/initiatives/${planUpgrade.initiativeId}/subscription/upgrade/${planUpgrade.featureCode}`,
            method: 'post',
            data: planUpgrade,
          };
        },
        invalidatesTags: ['initiative-upgrade'],
      }),
      previewPlan: builder.mutation<PreviewUpgradeResult, PlanUpgrade>({
        transformResponse,
        query: (planUpgrade) => {
          return {
            url: `/initiatives/${planUpgrade.initiativeId}/subscription/upgrade/${planUpgrade.featureCode}/preview`,
            method: 'post',
            data: planUpgrade,
          };
        },
      }),
      addPaymentDetails: builder.mutation<{ url: string }, { initiativeId: string; returnUrl: string }>({
        transformResponse,
        query: ({ initiativeId, returnUrl }) => ({
          url: `/initiatives/${initiativeId}/subscription/add-payment-details`,
          method: 'post',
          data: { returnUrl },
        }),
      }),
      integrateCompanyTracker: builder.mutation<
        void,
        { initiativeId: string; unitConfig: Pick<UnitConfig, 'currency'> }
      >({
        transformResponse,
        query: ({ initiativeId, unitConfig }) => ({
          url: `/initiatives/${initiativeId}/app-integration/company-tracker`,
          method: 'post',
          data: { unitConfig },
        }),
      }),
      integrateMaterialityTracker: builder.mutation<void, { initiativeId: string }>({
        transformResponse,
        query: ({ initiativeId }) => ({
          url: `/initiatives/${initiativeId}/app-integration/materiality-tracker`,
          method: 'post',
        }),
      }),
      updateMateriality: builder.mutation<void, { initiativeId: string; materiality: MaterialityMap | undefined }>({
        transformResponse,
        query: ({ initiativeId, materiality }) => ({
          url: `/initiatives/${initiativeId}/materiality`,
          method: 'patch',
          data: materiality,
        }),
      }),
      /**
       * Fetches minimal metric group data for dropdown/filter components.
       * Returns only fields needed for UI: _id, groupName, groupData, universalTrackers, accessType.
       */
      getMetricGroupsDropdown: builder.query<MetricGroupDropdownItem[], string>({
        transformResponse,
        query: (initiativeId) => ({
          url: `/initiatives/${initiativeId}/metric-groups/dropdown`,
          method: 'get',
        }),
        providesTags: ['metric-groups-dropdown'],
      }),
    }),
  });

export const {
  useGetInitiativeByIdQuery,
  useGetAvailablePeriodsQuery,
  useUpgradePlanMutation,
  usePreviewPlanMutation,
  useGetUpgradeDetailsQuery,
  useAddPaymentDetailsMutation,
  useIntegrateCompanyTrackerMutation,
  useIntegrateMaterialityTrackerMutation,
  useUpdateMaterialityMutation,
  useGetMetricGroupsDropdownQuery,
} = initiativeApi;
